/*
  * This file is intended to bridge constants and types
  * across various apps/systems including:
  *
  * lambdas
  * next
  * sst deployment
  *
*/
import { z } from 'zod';

export type ModelProvider = 'openai' | 'azure' | 'bedrock' | 'together' | 'nomic' | 'google';

export type ModelType = 'completions' | 'embeddings' | 'ingestion-small' | 'embeddings-bulk' | 'embeddings-regular' | 'embeddings-webapp';

// DDB storage keys
export const MATTER_STATUS_HASH_KEY = "accountId";
export const MATTER_STATUS_RANGE_KEY = "matterId";
export const ACCOUNT_STATUS_HASH_KEY = "accountId";
export const API_FAILURES_HASH_KEY = "yyyy-mm-dd-hh-m";
export const API_FAILURES_RANGE_KEY = "ttl"; // PDN TTL epoch as range
export const INGESTION_PROGRESS_HASH_KEY = "accountId";
export const INGESTION_PROGRESS_RANGE_KEY = "matterId";
export const INGESTION_METADATA_HASH_KEY = "id";
export const INGESTION_METADATA_RANGE_KEY = "metadataId";
export const INGESTION_METADATA_DATA_KEY = "dataJson";
export const INGESTION_METADATA_WELL_KNOWN_MODEL = "model";
export const INGESTION_METADATA_WELL_KNOWN_ACCOUNT_DB_MIGRATION_TS = "account-db-migration-ts";
export const INGESTION_METADATA_WELL_KNOWN_MATTER_DB_MIGRATION_TS = "matter-db-migration-ts";
export const INGESTION_METADATA_WELL_KNOWN_KL_DB_MIGRATION_TS = "kl-db-migration-ts";
export const UNKNOWN_DOC_TYPES_HASH_KEY = "docType";
export const INGESTION_METADATA_WELL_KNOWN_FILE_MANIFEST = "file-manifest";
export const QUEUE_INFO_WELL_KNOWN_KEY = "queueInfo"
export const FILE_DEDUPE_HASH_KEY = "fileId";

// mass ingestion params - do not decrease MASS_INGESTION_SEGMENT_COUNT without draining and pausing queues
export const MASS_INGESTION_TOPIC_PREFIX = "MassSegmentedIngestion";
export const MASS_INGESTION_SEGMENT_COUNT = 11;
export const MASS_INGESTION_TOPIC_SUFFIX = "Topic";

// regular ingestion params - do not decrease REG_INGESTION_SEGMENT_COUNT without draining and pausing queues
export const REG_INGESTION_TOPIC_PREFIX = "RegSegmentedIngestion";
export const REG_INGESTION_SEGMENT_COUNT = 11;
export const REG_INGESTION_TOPIC_SUFFIX = "Topic";

// magic stings
export const AZURE_OPENAI_BASE_PATH_ENV_VAR = "AZURE_OPENAI_BASE_PATH";
export const NEXT_PUBLIC_COMPLETIONS_PROVIDER_ENV_VAR = "NEXT_PUBLIC_COMPLETIONS_PROVIDER";
export const EMBEDDINGS_PROVIDER_ENV_VAR = "EMBEDDINGS_PROVIDER"
export const COMPLETIONS_PROVIDER_ENV_VAR = "COMPLETIONS_PROVIDER"
export const INGESTER_TYPE_ENV_VAR = "INGESTER_TYPE";
export const INGESTER_DEBUG_MODE = "INGESTER_DEBUG_MODE";
export const PG_HOST_ENV_VAR = "PG_HOST";
export const PG_RO_HOST_ENV_VAR = "PG_RO_HOST";
export const PG_USER_ENV_VAR = "PG_USER";
export const PG_RO_USER_ENV_VAR = "PG_RO_USER";
export const PG_DB_ENV_VAR = "PG_DB";
export const SB_ENV_ENV_VAR = "NEXT_PUBLIC_SB_ENV";
export const SMOKEBALL_API_URL_ENV_VAR = "SMOKEBALL_API_URL";
export const CLIENT_CREDENTIALS_CLIENT_ID_ENV_VAR = "CLIENT_CREDENTIALS_CLIENT_ID";
export const CLIENT_CREDENTIALS_CLIENT_SECRET_ENV_VAR = "CLIENT_CREDENTIALS_CLIENT_SECRET";
export const CLIENT_CREDENTIALS_URL_ENV_VAR = "CLIENT_CREDENTIALS_URL";

export const MAX_FILES_MASS_INGEST = 2000; // if you alter this update MAX_FILES_ADJEST_TS
export const MAX_FILES_PRIO_INGEST = 10000; // if you alter this update MAX_FILES_ADJEST_TS
export const MAX_FILES_INGEST_HARD_CAP = 20000; // if you alter this update MAX_FILES_ADJEST_TS
export const MAX_FILES_ADJUST_TS = 1723783242237; // node msepick ts
export const MAX_FILES_EXEMPTION_WHITELIST = ["673af793-7a1e-4dd9-8eef-5873e411c56c"];

export const LARGE_FILE_THRESHOLD = 100000; // 1M
export const VERY_LARGE_FILE_THRESHOLD = 1000000; // 10M

export const matterIngestionStatus = {
  "OK": "Normal operation",
  "ERRPREMASS": "Matter has too many files to process",
  "ERRPREPRIO": "Matter has too many files to process",
  "ERRPOST": "Matter has reached maximum amount of files that can be processed",
} as const;
export type TmatterIngestionStatus = keyof typeof matterIngestionStatus;

export const isFargate = () => {
  return process.env?.AWS_EXECUTION_ENV === "AWS_ECS_FARGATE";
}

const ingesterTypes = ['regular', 'mass'] as const;
export type TIngesterType = typeof ingesterTypes[number];
export const getIngesterType = (): TIngesterType => {
  switch (process.env[INGESTER_TYPE_ENV_VAR]) {
    case 'regular': { return 'regular' }
    case 'mass': { return 'mass' }
    default: {
      throw new Error(`Unknown ingester type: ${process.env[INGESTER_TYPE_ENV_VAR]}`)
    }
  }
}

export const safeGetIngesterType = (): TIngesterType | undefined => {
  try {
    return getIngesterType();
  } catch {
    return undefined;
  }
}

export const getSegmentedResourceNames = (iType: TIngesterType, priority?: boolean) => {
  let segmentCount: number;
  let prefix: string;

  switch (iType || getIngesterType()) {
    case "mass": {
      segmentCount = MASS_INGESTION_SEGMENT_COUNT;
      prefix = `${priority ? 'P' : ''}${MASS_INGESTION_TOPIC_PREFIX}`;
      break;
    }
    case "regular": {
      segmentCount = REG_INGESTION_SEGMENT_COUNT;
      prefix = REG_INGESTION_TOPIC_PREFIX;
      break;
    }
    default: {
      throw new Error(`unhandled ingester type ${getIngesterType()}`)
    }
  }

  segmentCount = Math.floor(segmentCount);
  if (segmentCount < 1) {
    throw new Error(`invalid segment count, ${segmentCount}`)
  }

  const namesWithoutSuffix: string[] = [];
  for (let i = 0; i < segmentCount; i++) {
    namesWithoutSuffix.push(`${prefix}${i}`);
  }

  return namesWithoutSuffix;
}

export const getIngesterEmbeddingsModelType = (): ModelType => {
  switch (getIngesterType()) {
    case 'regular': {
      return 'embeddings-regular'
    }
    case 'mass': {
      return 'embeddings-bulk'
    }
    default: {
      throw new Error(`Unknown ingester type: ${getIngesterType()}`)
    }
  }
}

// external data coming in matches these patterns.
export interface ITracking {
  trackingId: string;
  trackingPath: string;
}
export interface IAccountRequest extends ITracking {
  accountId: string;
  forceAll?: boolean; // used to force ingestion of all matters from account
}

export interface ITrackedMatter extends ITracking {
  matterId: string;
}

export interface ITrackedMatterFile extends ITrackedMatter {
  fileId: string;
}

export interface IMatterRequest extends IAccountRequest {
  matterId: string;
  priority?: boolean;
}

export interface IKnowledgeLibraryRequest extends IAccountRequest {
  klId: string;
  priority?: boolean;
}

export const bdaChunkMetadataSchema = z.object({
  docTitle: z.string().optional(),
  sectionPath: z
    .object({ formatted: z.string(), raw: z.array(z.string()) })
    .optional(),
  pageNumbers: z
    .object({ formatted: z.string(), raw: z.array(z.number()) })
    .optional(),
  joinedTableIndex: z.number().optional(),
  joinedTableIndexOf: z.number().optional(),
});
export type BdaChunkMetadata = z.infer<typeof bdaChunkMetadataSchema>;

export const chunkMetadataEmailSchema = z.object({
  "Doc Type": z.literal("email"),
  From: z.string(),
  To: z.string(),
  Subject: z.string(),
  Attachments: z.string(),
});

export const chunkMetadataEmailWithTypeSchema = chunkMetadataEmailSchema.extend(
  {
    type: z.literal("email"),
  },
);
export type ChunkMetadataEmail = z.infer<
  typeof chunkMetadataEmailWithTypeSchema
>;

export const chunkMetadataDefaultSchema = z.object({
  Filename: z.string(),
  "Doc Type(s)": z.string(),
});

export const chunkMetadataDefaultWithTypeSchema =
  chunkMetadataDefaultSchema.extend({
    type: z.literal("default"),
  });
export type ChunkMetadataDefault = z.infer<
  typeof chunkMetadataDefaultWithTypeSchema
>;

export const chunkMetadataBdaEmailSchema = chunkMetadataEmailSchema
  .merge(bdaChunkMetadataSchema)
  .extend({
    type: z.literal("bda-email"),
  });
export type ChunkMetadataBdaEmail = z.infer<typeof chunkMetadataBdaEmailSchema>;

export const chunkMetadataBdaDefaultSchema = chunkMetadataDefaultSchema
  .merge(bdaChunkMetadataSchema)
  .extend({
    type: z.literal("bda-default"),
  });
export const chunkMetadataBdaKLSchema = bdaChunkMetadataSchema
  .extend({
    type: z.literal("bda-kl"),
  });
export type ChunkMetadataBdaDefault = z.infer<
  typeof chunkMetadataBdaDefaultSchema
>;

export const chunkMetadataMarkdownNonMatterFileSchema = z.object({
  type: z.literal("markdown-non-matter-file"),
  sectionPath: z.object({ formatted: z.string(), raw: z.array(z.string()) }).optional(),
  pageNumbers: z.object({ formatted: z.string(), raw: z.array(z.number()) }).optional(),
  joinedTableIndex: z.number().optional(),
  joinedTableIndexOf: z.number().optional(),
});
export type ChunkMetadataNonMatterFileMarkdown = z.infer<typeof chunkMetadataMarkdownNonMatterFileSchema>;

export const chunkMetadataMarkdownMatterFileSchema = z.object({
  type: z.literal("markdown-matter-file"),
  sectionPath: z.object({ formatted: z.string(), raw: z.array(z.string()) }).optional(),
  pageNumbers: z.object({ formatted: z.string(), raw: z.array(z.number()) }).optional(),
  joinedTableIndex: z.number().optional(),
  joinedTableIndexOf: z.number().optional(),
  Filename: z.string(),
  "Doc Type(s)": z.string(),
});
export type ChunkMetadataMatterFileMarkdown = z.infer<typeof chunkMetadataMarkdownMatterFileSchema>;

export const chunkMetadataNoMetadataSchema = z.object({
  type: z.literal("no-metadata"),
});

export type ChunkMetadataNone = z.infer<
  typeof chunkMetadataNoMetadataSchema
>;

export const chunkMetadataSchema = z.discriminatedUnion("type", [
  chunkMetadataDefaultWithTypeSchema,
  chunkMetadataEmailWithTypeSchema,
  chunkMetadataBdaDefaultSchema,
  chunkMetadataBdaEmailSchema,
  chunkMetadataNoMetadataSchema,
  chunkMetadataBdaKLSchema,
  chunkMetadataMarkdownNonMatterFileSchema,
  chunkMetadataMarkdownMatterFileSchema,
])
export type ChunkMetadata = z.infer<typeof chunkMetadataSchema>;

export const chunkSchema = z.object({
  content: z.string(),
  metadata: chunkMetadataSchema,
});

export const chunkMetadataHasSectionPathProp = (
  chunkMetadata: ChunkMetadata,
): chunkMetadata is ChunkMetadataBdaDefault | ChunkMetadataBdaEmail =>
  "sectionPath" in chunkMetadata;

export const chunkMetadataHasDocTitleProp = (
  chunkMetadata: ChunkMetadata,
): chunkMetadata is ChunkMetadataBdaDefault | ChunkMetadataBdaEmail =>
  "docTitle" in chunkMetadata;

export const chunkMetadataHasPageNumbersProp = (
  chunkMetadata: ChunkMetadata,
): chunkMetadata is ChunkMetadataBdaDefault | ChunkMetadataBdaEmail =>
  "pageNumbers" in chunkMetadata;

export const chunkMetadataHasJoinedTableIndexProp = (
  chunkMetadata: ChunkMetadata,
): chunkMetadata is ChunkMetadataBdaDefault | ChunkMetadataBdaEmail =>
  "joinedTableIndex" in chunkMetadata;

export type Chunk = z.infer<typeof chunkSchema>;

// https://bitbucket.org/smokeballdev/international/src/develop/pd/matter%20management/ui/ManageFiles2/DocumentSearch.Core/IIndexingNotificationService.cs
export const upstreamExtractionTypes = {
  "default": "default",
  "bedrockDA": "bedrockDA",
  "BedrockDA": "BedrockDA", // this may come from KL because suffic is _BedrockDA
  "markdown": "markdown",
  "Markdown": "Markdown",
  "markdown-file": "markdown-file",
} as const;

export type UpstreamExtractionTypeKey = keyof typeof upstreamExtractionTypes;

export const chunksAreMarkdown = (type: ChunkMetadata['type']) => {
  switch (type) {
    case "bda-email":
    case "bda-default":
    case "bda-kl":
    case "markdown-non-matter-file":
    case "markdown-matter-file":
      return true
    default:
      return false
  }
}

export const upstreamExtractionTypePersistsMetadata = (t?: UpstreamExtractionTypeKey): boolean => {
  const extype = t === undefined ? upstreamExtractionTypes.default : t;
  if (!Object.values(upstreamExtractionTypes).includes(extype)) {
    throw new Error(`unknown upstream extraction type ${t}`);
  }

  if (extype === 'Markdown' || extype === 'markdown' || extype === 'markdown-file') {
    return true;
  } else if (extype === "bedrockDA") {
    return true;
  } else if (extype === "BedrockDA") {
    return true;
  } else if (extype === "default") {
    return false;
  } else {
    const _exhaustiveCheck: never = extype;
    throw new Error(`hey shouldn't get here if ${_exhaustiveCheck} is used.`)
  }
}

const extractionTypes = ["NORMAL", "InfotrackCR", "BDA", "Markdown"] as const;
export type ExtractionType = typeof extractionTypes[number]
export type ExtractionEvaluation = { extractionType: ExtractionType, forced: boolean, ingesterType: TIngesterType };
// { [ExtractionType] : { [magic key] : [string representation of file]}}
export type TAdditionalFiles = Partial<Record<ExtractionType, Record<string, string>>>;

export interface IFileRequest extends IMatterRequest {
  fileId: string;
  fileVersionId?: string; // should generally be present in regular but not in mass

  documentSource: 'opensearch' | 'rds' | 'kl';
  ingestionType: 'regular' | 'bulk';
  operation?: 'uploaded' | 'deleted' | 'matterIdUpdated'; // only applicable for regular ingestion
  forceExtraction?: ExtractionType; // this is our stuff where we detect/force a particular extraction type
  extractionType?: UpstreamExtractionTypeKey; // this is dotnet extraction type (extract text for opensearch)
  fastTrack?: boolean;
}

const ingestionProcessingStatuses = ['errored', 'rejected', 'requeued', 'ingested'] as const;
export type IngestionProcessingStatus = typeof ingestionProcessingStatuses[number];
export interface IngestionProcessingUpdate extends ITracking {
  request: IFileRequest;
  status: IngestionProcessingStatus;
  message?: string;
}

export const assertIngestionProcessingUpdate = (thing: unknown): IngestionProcessingUpdate => {
  assertTracking(thing);
  const maybeIngestionProcessingUpdate = thing as IngestionProcessingUpdate;
  if (!maybeIngestionProcessingUpdate || typeof maybeIngestionProcessingUpdate !== 'object') {
    throw new Error(
      `Does not implement IngestionProcessingUpdate interface: ${dumpVal(thing)}`,
    );
  }
  const { request, status, message } = maybeIngestionProcessingUpdate;
  assertFileReq(request);
  if (!ingestionProcessingStatuses.includes(status)) {
    throw new Error(
      `Does not implement IngestionProcessingUpdate interface: ${dumpVal(thing)}`,
    );
  }

  if (typeof message === 'string' || typeof message === 'undefined') {
    return maybeIngestionProcessingUpdate;
  }

  throw new Error(
    `Does not implement IngestionProcessingUpdate interface: ${dumpVal(thing)}`,
  );
};

export interface IEvaluationRequest extends IFileRequest {
  evaluation: ExtractionEvaluation;
}

export interface EnableAccountCommand extends IAccountRequest {
  command: "ENABLE_ACCOUNT";
}
export interface DisableAccountCommand extends IAccountRequest {
  command: "DISABLE_ACCOUNT";
}
export interface IngestMatterCommand extends IMatterRequest {
  command: "INGEST_MATTER";
}
export interface DisableMatterCommand extends IMatterRequest {
  command: "DISABLE_MATTER";
}
export interface IngestAllMattersCommand extends IAccountRequest {
  command: "INGEST_ALL_MATTERS";
}
export interface ResetAccountCommand extends IAccountRequest {
  command: "RESET_ACCOUNT";
}
export interface PurgeAccountFromPostgresCommand extends IAccountRequest {
  command: "PURGE_ACCOUNT_FROM_POSTGRES";
}
export interface ResetMatterCommand extends IMatterRequest {
  command: "RESET_MATTER";
  skipPostgresPurge: boolean;
}
export interface PurgeMatterFromPostgresCommand extends IMatterRequest {
  command: "PURGE_MATTER_FROM_POSTGRES";
}
type PurgeFileFromDynamoPayload = Omit<IFileRequest, "documentSource" | "ingestionType">;
export interface PurgeFileFromDynamoCommand extends PurgeFileFromDynamoPayload {
  command: "PURGE_FILE_FROM_DYNAMO";
}
export interface IngestKLCommand extends IKnowledgeLibraryRequest {
  command: "INGEST_KL";
}
export interface DisableKLCommand extends IKnowledgeLibraryRequest {
  command: "DISABLE_KL";
}
export interface ResetKLCommand extends IKnowledgeLibraryRequest {
  command: "RESET_KL";
  skipPostgresPurge: boolean;
}
export interface PurgeKLFromPostgresCommand extends IKnowledgeLibraryRequest {
  command: "PURGE_KL_FROM_POSTGRES";
}

export type TDashboardCommand =
  | EnableAccountCommand
  | DisableAccountCommand
  | IngestMatterCommand
  | DisableMatterCommand
  | IngestAllMattersCommand
  | ResetAccountCommand
  | PurgeAccountFromPostgresCommand
  | ResetMatterCommand
  | PurgeMatterFromPostgresCommand
  | PurgeFileFromDynamoCommand
  | IngestKLCommand
  | DisableKLCommand
  | ResetKLCommand
  | PurgeKLFromPostgresCommand

const dumpVal = (thing: unknown): string => {
  let val = `${thing}`;
  try {
    val = JSON.stringify(thing);
  } catch {
    // pass
  }

  return `type: ${typeof thing}, value: ${val}`;
};

export const assertTracking = (thing: unknown): ITracking => {
  if (thing && typeof thing === "object") {
    const maybeTracking = thing as ITracking;
    const { trackingPath, trackingId } = maybeTracking;
    if (
      trackingId &&
      trackingPath &&
      typeof trackingId === "string" &&
      typeof trackingPath === "string"
    ) {
      return maybeTracking;
    }
  }

  throw new Error(`Does not implement ITracking interface: ${dumpVal(thing)}`);
};

export const assertAccountReq = (thing: unknown): IAccountRequest => {
  assertTracking(thing);
  const maybeAcReq = thing as IAccountRequest;
  if (maybeAcReq.accountId && typeof maybeAcReq.accountId === "string") {
    return maybeAcReq;
  }

  throw new Error(
    `Does not implement IAccountRequest interface: ${dumpVal(thing)}`,
  );
};

export const assertMatterReq = (thing: unknown): IMatterRequest => {
  assertAccountReq(thing);
  const maybeMatReq = thing as IMatterRequest;
  if (maybeMatReq.matterId && typeof maybeMatReq.matterId === "string") {
    return maybeMatReq;
  }

  throw new Error(
    `Does not implement IMatterRequest interface: ${dumpVal(thing)}`,
  );
};

export const assertFileReq = (thing: unknown): IFileRequest => {
  assertMatterReq(thing);
  const maybeFileReq = thing as IFileRequest;
  if (maybeFileReq.fileId && typeof maybeFileReq.fileId === "string") {
    return maybeFileReq;
  }

  throw new Error(
    `Does not implement IFileRequest interface: ${dumpVal(thing)}`,
  );
};

export const assertExtractionEvaluationRequest = (thing: unknown): IEvaluationRequest => {
  assertFileReq(thing);
  const maybeExtEval = thing as IEvaluationRequest;
  const evaluation = maybeExtEval.evaluation;

  if (typeof evaluation !== 'object') {
    throw new Error(
      `Does not implement ExtractionEvaluation interface: ${dumpVal(thing)}`,
    );
  }

  const extractionType = evaluation.extractionType;
  const ingesterType = evaluation.ingesterType;
  const forced = evaluation.forced;

  if (typeof forced !== 'boolean' || typeof ingesterType !== 'string' || typeof extractionType !== 'string') {
    throw new Error(
      `Does not implement ExtractionEvaluation interface: ${dumpVal(thing)}`,
    );
  }

  if (
    !ingesterTypes.includes(ingesterType as TIngesterType) ||
    !extractionTypes.includes(extractionType as ExtractionType)
  ) {
    throw new Error(
      `Does not implement ExtractionEvaluation interface: ${dumpVal(thing)}`,
    );
  }

  return maybeExtEval;
};

interface TLogShared {
  message: string;
  details?: unknown;
}

export interface TLogInfo extends TLogShared {
  error?: unknown;
}

export type TLogWarn = TLogInfo;

export interface TLogError extends TLogShared {
  error: unknown;
}

export type TMinimalMetric = {
  timestamp: string;
  level: string;
  trackingId: string;
  trackingPath: string;
  message: {
    code: string,
    extra?: Record<string, string | number>
  };
}

export type TLogger = {
  getTracking: () => ITracking;
  makeChildLogger: (nextSlug: string) => TLogger;
  debug: (o: TLogInfo | string) => void;
  info: (o: TLogInfo | string) => void;
  warn: (o: TLogWarn | string) => void;
  error: (o: TLogError) => void;
  dynamic?: TLogger;
  makeMetric: {
    debug: (code: string, extra?: Record<string, string | number>) => TMinimalMetric;
    info: (code: string, extra?: Record<string, string | number>) => TMinimalMetric;
    warn: (code: string, extra?: Record<string, string | number>) => TMinimalMetric;
    error: (code: string, extra?: Record<string, string | number>) => TMinimalMetric;
  }
};

export const openSearchMatterFile = z.object({
  fileId: z.string(),
  accountId: z.string(),
  fileVersionId: z.string(),
  matterId: z.string(),
  dateCreated: z.string(),
  dateModified: z.string(),
  fileExtension: z.string(),
  fileName: z.string(),
  content: z.string(),
  extractionType: z.string().optional().nullable(),
  extractionTimestamp: z.string().optional().nullable(),
  additional: z.object({
    pages: z.number().optional().nullable(),
    confidence: z.number().optional().nullable(),
  }).optional().nullable(),
});

export type OpenSearchMatterFileExclEmailProps = z.infer<typeof openSearchMatterFile>

export interface OpenSearchMatterFile extends OpenSearchMatterFileExclEmailProps {
  emailSubject: string;
  emailTo: string;
  emailCc: string;
  emailFrom: string;
  emailAttachments: string;
  emailSent: string;
  rawContent: string;
  source: "opensearch" | "extractedtext";
}

export type RecordType = 'chunk' | 'doc-summary' | 'hyp-question-chunk' | 'hyp-question-summ' | 'indexed-flag';
type BaseMatterFileMetadataProps = Omit<
  OpenSearchMatterFileExclEmailProps,
  "content" | "dateCreated" | "dateModified"
>;

export interface MatterFileMetadata extends BaseMatterFileMetadataProps {
  // text: string;
  // textCompressed: string;
  id: string;
  docType: string;
  additionalDocType: string;
  dateCreated: number;
  dateModified: number;
  recordType: RecordType;
  created_at?: string;
  updated_at?: string;
};

export type ModelKey =
  'openai_gpt_4_1106' |
  'azure_gpt_4_1' |
  'azure_model_router' |
  'openai_gpt_4o' |
  'openai_gpt_4o_mini' |
  'azure_gpt_4o' |
  'azure_gpt_4o_2024_08_06' |
  'azure_gpt_4o_mini' |
  'azure_gpt_4_0613' |
  'azure_gpt_4_1106' |
  'azure_gpt_o3_mini' |
  'azure_gpt_o3' |
  'azure_gpt_o4_mini' |
  'openai_gpt_35_turbo_16k' |
  'openai_gpt_5' |
  'azure_gpt_5' |
  'azure_gpt_35_turbo_16k' |
  'bedrock_claude_3_haiku' |
  'bedrock_claude_3_sonnet' |
  'bedrock_claude_3_5_sonnet' |
  'bedrock_claude_3_opus' |
  'bedrock_llama_3_70b_instruct' |
  'bedrock_llama_3_8b_instruct' |
  'google_gemini-2.5-pro-preview-03-25' |
  'google_gemini_2.5_flash' |
  'openai_ada' |
  'azure_ada' |
  'nomicv1' |
  'bedrock_claude_3_haiku_completions' |
  'bedrock_claude_3_7_sonnet' |
  'azure_foundry_grok_3' |
  'bedrock_claude_opus_4' |
  'bedrock_claude_sonnet_4'

export const getEmbedsModelDimensions = (modelKey: ModelKey): number => {
  switch (modelKey) {
    case 'openai_ada':
    case 'azure_ada':
      return 1536;
    case 'nomicv1':
      return 768;
    default:
      throw new Error(`unknown modelKey for getEmbedsModelDimensions: ${modelKey}`);
  }
}

// takes the (context window - max output tokens) * 0.95 (to be safe)
export const getMaxInputTokens = (modelKey: ModelKey): number => {
  switch (modelKey) {
    case 'azure_gpt_35_turbo_16k':
    case 'openai_gpt_35_turbo_16k':
      return Math.floor((16385 - 4096) * 0.95);
    case 'azure_gpt_4_0613':
      return Math.floor((8192 - 4096) * 0.95);
    case 'azure_gpt_4_1106':
    case 'openai_gpt_4_1106':
    case 'azure_gpt_4o':
    case 'azure_gpt_4o_2024_08_06':
    case 'azure_gpt_4o_mini':
    case 'openai_gpt_4o':
    case 'openai_gpt_4o_mini':
    case 'azure_gpt_o3':
    case 'azure_gpt_o4_mini':
      return Math.floor((128000 - 4096) * 0.95);
    case 'bedrock_claude_3_haiku':
    case 'bedrock_claude_3_opus':
    case 'bedrock_claude_3_sonnet':
    case 'bedrock_claude_3_haiku_completions':
    case 'bedrock_claude_opus_4':
    case 'bedrock_claude_sonnet_4':
      return Math.floor((200000 - 8192) * 0.95);
    case 'bedrock_llama_3_70b_instruct':
    case 'bedrock_llama_3_8b_instruct':
      return Math.floor((8000 - 4096) * 0.95);
    case 'google_gemini-2.5-pro-preview-03-25':
    case 'google_gemini_2.5_flash':
      return Math.floor((1000000 - 4096) * 0.95);
    case 'azure_foundry_grok_3':
      return Math.floor((131072 - 4096) * 0.95);
    case 'openai_gpt_5':
    case 'azure_gpt_5':
      return Math.floor((272000 - 4096) * 0.95);
    default:
      throw new Error(`unknown modelKey for getMaxInputTokens: ${modelKey}`);
  }
}

export interface IIngestionMetadataModel {
  version: string;
}

export interface IIngestionMetadataModelV1 extends IIngestionMetadataModel {
  version: "v1";
  embeddingsModelKey: ModelKey;
  llmModelKey: ModelKey;
}

export const isIngestionMetadataV1 = (thing: unknown): thing is IIngestionMetadataModelV1 => typeof thing === 'object' && thing != null && (thing as Record<string, string>).version === "v1";

export interface IIngestionMetadataModelV2 extends IIngestionMetadataModel {
  version: "v2";
  docTypes: ModelKey;
  docTypesFixing: ModelKey;
  docSummary: ModelKey;
  docSummaryFixing: ModelKey;
  embeds: ModelKey;
}

export const isIngestionMetadataV2 = (thing: unknown): thing is IIngestionMetadataModelV2 => typeof thing === 'object' && thing != null && (thing as Record<string, string>).version === "v2";

export interface IIngestionMetadataModelV3 extends IIngestionMetadataModel {
  version: "v3";
  ts: number;
  docTypes: ModelKey;
  docTypesFixing: ModelKey;
  docSummary: ModelKey;
  docSummaryFixing: ModelKey;
  embeds: ModelKey;
}

export const isIngestionMetadataV3 = (thing: unknown): thing is IIngestionMetadataModelV3 => typeof thing === 'object' && thing != null && (thing as Record<string, string>).version === "v3";

export interface IModelInfo {
  key: ModelKey;
  version: string;
  temperature: number;
  default: boolean;
  ts: number;
}

export interface IIngestionMetadataModelV4 extends IIngestionMetadataModel {
  version: "v4";
  ts: number;
  docTypes: IModelInfo[];
  docTypesFixing: IModelInfo[];
  docSummary: IModelInfo[];
  docSummaryFixing: IModelInfo[];
  chunks: IModelInfo[];
  embeds: IModelInfo[];
}

export const isIngestionMetadataV4 = (thing: unknown): thing is IIngestionMetadataModelV4 => typeof thing === 'object' && thing != null && (thing as Record<string, string>).version === "v4";

export type VectorDB = 'pgvector';
export const LATEST_VECTOR_DB: VectorDB = 'pgvector';

export interface IIngestionMetadataModelV5 extends IIngestionMetadataModel {
  version: "v5";
  ts: number;
  docTypes: IModelInfo[];
  docTypesFixing: IModelInfo[];
  docSummary: IModelInfo[];
  docSummaryFixing: IModelInfo[];
  chunks: IModelInfo[];
  embeds: IModelInfo[];
  vectordb: VectorDB;
}

export const isIngestionMetadataV5 = (thing: unknown): thing is IIngestionMetadataModelV5 => typeof thing === 'object' && thing != null && (thing as Record<string, string>).version === "v5";

export const METADATA_VERSIONS = ['v1'] as const;
export type MetadataVersion = typeof METADATA_VERSIONS[number];
export const assertMetadataVersion = (thing: unknown): MetadataVersion => {
  if (typeof thing === 'string' && METADATA_VERSIONS.includes(thing as typeof METADATA_VERSIONS[number])) {
    return thing as MetadataVersion;
  }
  throw new Error(`invalid MetadataVersion: ${JSON.stringify(thing)}`);
}
export const METADATA_LATEST_VERSION: MetadataVersion = 'v1'

export const SUMMARY_VERSIONS = ['v1'] as const;
export type SummaryVersion = typeof SUMMARY_VERSIONS[number];
export const assertSummaryVersion = (thing: unknown): SummaryVersion => {
  if (typeof thing === 'string' && SUMMARY_VERSIONS.includes(thing as typeof SUMMARY_VERSIONS[number])) {
    return thing as SummaryVersion;
  }
  throw new Error(`invalid SummaryVersion: ${JSON.stringify(thing)}`);
}
export const SUMMARY_LATEST_VERSION: SummaryVersion = 'v1'

export const FILE_CHUNKS_VERSIONS = ['v1', 'v2'] as const;
export type FileChunksVersion = typeof FILE_CHUNKS_VERSIONS[number];
export const assertFileChunksVersion = (thing: unknown): FileChunksVersion => {
  if (typeof thing === 'string' && FILE_CHUNKS_VERSIONS.includes(thing as typeof FILE_CHUNKS_VERSIONS[number])) {
    return thing as FileChunksVersion;
  }
  throw new Error(`invalid FileChunksVersion: ${JSON.stringify(thing)}`);
}
export const FILE_CHUNKS_LATEST_VERSION: FileChunksVersion = 'v2'

// v1: ada
// v2: nomicv1
export const EMBEDS_VERSIONS = ['v1', 'v2'] as const;
export type EmbedsVersion = typeof EMBEDS_VERSIONS[number];
export const assertEmbedsVersion = (thing: unknown): EmbedsVersion => {
  if (typeof thing === 'string' && EMBEDS_VERSIONS.includes(thing as typeof EMBEDS_VERSIONS[number])) {
    return thing as EmbedsVersion;
  }
  throw new Error(`invalid EmbedsVersion: ${JSON.stringify(thing)}`);
}
export const LATEST_EMBEDS_VERSION: EmbedsVersion = 'v2'

export const ingestLLMModelKeyTypes = ['docTypes', 'docSummary', 'docTypesFixing', 'docSummaryFixing', 'chunks'] as const;
export type IngestLLMModelKeyType = typeof ingestLLMModelKeyTypes[number];
export const ingestEmbedModelKeyTypes = ['embeds'] as const;
export type IngestEmbedModelKeyType = typeof ingestEmbedModelKeyTypes[number];

export const buildAzureModelInfo = (): IIngestionMetadataModelV3 => ({
  version: "v3",
  ts: Date.now(),
  docTypes: 'azure_gpt_35_turbo_16k',
  docTypesFixing: 'azure_gpt_35_turbo_16k',
  docSummary: 'azure_gpt_35_turbo_16k',
  docSummaryFixing: 'azure_gpt_35_turbo_16k',
  embeds: 'azure_ada',
})

export const buildAwsModelInfo = (): IIngestionMetadataModelV3 => ({
  version: "v3",
  ts: Date.now(),
  docTypes: 'bedrock_claude_3_haiku',
  docTypesFixing: 'bedrock_claude_3_haiku',
  docSummary: 'bedrock_claude_3_haiku',
  docSummaryFixing: 'bedrock_claude_3_haiku',
  embeds: 'nomicv1',
})

const getTemperatureForModelInfoItem = (modelInfoItem: IngestLLMModelKeyType | IngestEmbedModelKeyType): number => {
  switch (modelInfoItem) {
    case 'docTypes':
    case 'docTypesFixing':
    case 'chunks':
    case 'docSummary':
    case 'docSummaryFixing':
    case 'embeds':
      return 0;
    default:
      throw new Error(`unknown modelInfoItem: ${modelInfoItem}`);
  }
}

const getLatestVersionForModelInfoItem = (modelInfoItem: IngestLLMModelKeyType | IngestEmbedModelKeyType): string => {
  switch (modelInfoItem) {
    case 'docTypes':
    case 'docTypesFixing':
      return METADATA_LATEST_VERSION;
    case 'docSummary':
    case 'docSummaryFixing':
      return SUMMARY_LATEST_VERSION;
    case 'chunks':
      return FILE_CHUNKS_LATEST_VERSION;
    case 'embeds':
      return LATEST_EMBEDS_VERSION;
    default:
      throw new Error(`unknown modelInfoItem: ${modelInfoItem}`);
  }
}

export const buildLatestModelInfo = (): IIngestionMetadataModelV3 => buildAwsModelInfo()

export const upgradeV4toV5ModelInfo = (modelInfo: IIngestionMetadataModelV4, vectorDb?: VectorDB): IIngestionMetadataModelV5 => {
  return {
    version: "v5",
    ts: modelInfo.ts,
    docTypes: modelInfo.docTypes,
    docTypesFixing: modelInfo.docTypesFixing,
    docSummary: modelInfo.docSummary,
    docSummaryFixing: modelInfo.docSummaryFixing,
    chunks: modelInfo.chunks,
    embeds: modelInfo.embeds,
    vectordb: vectorDb ?? 'pgvector',
  }
}

export const upgradeV3toV4ModelInfo = (modelInfo: IIngestionMetadataModelV3): IIngestionMetadataModelV4 => {
  return {
    version: "v4",
    ts: modelInfo.ts,
    docTypes: [{ key: modelInfo.docTypes, default: true, temperature: getTemperatureForModelInfoItem('docTypes'), ts: modelInfo.ts, version: getLatestVersionForModelInfoItem('docTypes') }],
    docTypesFixing: [{ key: modelInfo.docTypesFixing, default: true, temperature: getTemperatureForModelInfoItem('docTypesFixing'), ts: modelInfo.ts, version: getLatestVersionForModelInfoItem('docTypesFixing') }],
    docSummary: [{ key: modelInfo.docSummary, default: true, temperature: getTemperatureForModelInfoItem('docSummary'), ts: modelInfo.ts, version: getLatestVersionForModelInfoItem('docSummary') }],
    docSummaryFixing: [{ key: modelInfo.docSummaryFixing, default: true, temperature: getTemperatureForModelInfoItem('docSummaryFixing'), ts: modelInfo.ts, version: getLatestVersionForModelInfoItem('docSummaryFixing') }],
    chunks: [{ key: modelInfo.docSummary, default: true, temperature: getTemperatureForModelInfoItem('chunks'), ts: modelInfo.ts, version: getLatestVersionForModelInfoItem('chunks') }],
    embeds: [{ key: modelInfo.embeds, default: true, temperature: getTemperatureForModelInfoItem('embeds'), ts: modelInfo.ts, version: 'v1' }],
  }
}

export const upgradeOldModelInfoToV4 = (modelInfo?: IIngestionMetadataModelV1 | IIngestionMetadataModelV2): IIngestionMetadataModelV4 => {
  const latestModelInfo = buildLatestModelInfo();
  const currentModelInfo: {
    docTypes: ModelKey;
    docTypesFixing: ModelKey;
    docSummary: ModelKey;
    docSummaryFixing: ModelKey;
    embeds: ModelKey;
  } = {
    docTypes: isIngestionMetadataV2(modelInfo) ? modelInfo.docTypes : isIngestionMetadataV1(modelInfo) ? modelInfo.llmModelKey : latestModelInfo.docTypes,
    docTypesFixing: isIngestionMetadataV2(modelInfo) ? modelInfo.docTypesFixing : isIngestionMetadataV1(modelInfo) ? modelInfo.llmModelKey : latestModelInfo.docTypesFixing,
    docSummary: isIngestionMetadataV2(modelInfo) ? modelInfo.docSummary : isIngestionMetadataV1(modelInfo) ? modelInfo.llmModelKey : latestModelInfo.docSummary,
    docSummaryFixing: isIngestionMetadataV2(modelInfo) ? modelInfo.docSummaryFixing : isIngestionMetadataV1(modelInfo) ? modelInfo.llmModelKey : latestModelInfo.docSummaryFixing,
    embeds: isIngestionMetadataV2(modelInfo) ? modelInfo.embeds : isIngestionMetadataV1(modelInfo) ? modelInfo.embeddingsModelKey : modelInfo ? 'azure_ada' : latestModelInfo.embeds,
  }

  const embedsVersion = currentModelInfo.embeds === 'azure_ada' ? 'v1' : (currentModelInfo.embeds === 'nomicv1' ? 'v2' : undefined);
  if (!embedsVersion) {
    throw new Error(`couldnt derive embedsVersion from currentModelInfo.embeds: ${currentModelInfo.embeds}`);
  }

  return {
    version: "v4",
    ts: 0,
    docTypes: [{ key: currentModelInfo.docTypes, ts: 0, default: true, temperature: getTemperatureForModelInfoItem('docTypes'), version: getLatestVersionForModelInfoItem('docTypes') }],
    docTypesFixing: [{ key: currentModelInfo.docTypesFixing, ts: 0, default: true, temperature: getTemperatureForModelInfoItem('docTypesFixing'), version: getLatestVersionForModelInfoItem('docTypesFixing') }],
    docSummary: [{ key: currentModelInfo.docSummary, ts: 0, default: true, temperature: getTemperatureForModelInfoItem('docSummary'), version: getLatestVersionForModelInfoItem('docSummary') }],
    docSummaryFixing: [{ key: currentModelInfo.docSummaryFixing, ts: 0, default: true, temperature: getTemperatureForModelInfoItem('docSummaryFixing'), version: getLatestVersionForModelInfoItem('docSummaryFixing') }],
    chunks: [{
      // file chunks does not use a model currently so just use docSummary to give it a valid model key
      key: currentModelInfo.docSummary,
      ts: 0,
      default: true,
      temperature: getTemperatureForModelInfoItem('chunks'),
      version: getLatestVersionForModelInfoItem('chunks'),
    }],
    embeds: [{ key: currentModelInfo.embeds, ts: 0, default: true, temperature: getTemperatureForModelInfoItem('embeds'), version: embedsVersion }],
  }
}

export const upgradeModelInfo = (modelInfoIn?: IIngestionMetadataModel | null): IIngestionMetadataModelV5 => {
  if (modelInfoIn) {
    if (isIngestionMetadataV5(modelInfoIn)) {
      return modelInfoIn;
    } else if (isIngestionMetadataV4(modelInfoIn)) {
      return upgradeV4toV5ModelInfo(modelInfoIn);
    } else if (isIngestionMetadataV3(modelInfoIn)) {
      return upgradeV4toV5ModelInfo(upgradeV3toV4ModelInfo(modelInfoIn));
    } else if (isIngestionMetadataV2(modelInfoIn) || isIngestionMetadataV1(modelInfoIn)) {
      return upgradeV4toV5ModelInfo(upgradeOldModelInfoToV4(modelInfoIn));
    } else {
      throw new Error(`Invalid model info version: ${JSON.stringify(modelInfoIn, null, 2)}`)
    }
  }

  return upgradeV4toV5ModelInfo(upgradeOldModelInfoToV4(), LATEST_VECTOR_DB);
}

export const searchSources = ['vectordb', 'es'] as const;
export type SearchSource = typeof searchSources[number];

export type LLMFlaggedIncoherentInput = {
  docType: 'incoherent';
  stepByStepReasoning: string;
  additionalDocType: null;
  incoherent: true;
}

export type TDocType = {
  stepByStepReasoning: string;
  docType: string;
  additionalDocType: string | null;
  incoherent?: boolean;
};

export type TDocTypes = Record<string, TDocType | LLMFlaggedIncoherentInput>;

export const isLLMFlaggedIncoherentInput = (thing: unknown): thing is LLMFlaggedIncoherentInput =>
  typeof thing === 'object' &&
  typeof (thing as Record<string, unknown>).incoherent === 'boolean' &&
  !!(thing as Record<string, boolean>).incoherent;

export interface IFileMD5Manifest {
  version: string;
}

export interface IFileMD5ManifestV1 extends IFileMD5Manifest {
  version: "v1";
  ts: number;
  contentFirst10kMd5: string;
  contentMd5: string;
  chunkMd5s: string[];
  docTypes: TDocTypes;
  summaryModelName: string;
  embedsModelName: string;
}

export interface IFileMD5ManifestV2 extends IFileMD5Manifest {
  version: "v2";
  ts: number;
  contentFirst10kMd5: string;
  contentMd5: string;
  chunkMd5s: string[];
  docTypes: TDocTypes;
  summaryModelName: string;
  embedsModelName: string;
  docTypesModelName: string;
  upstreamExtractionType?: UpstreamExtractionTypeKey;
}

type sstResourceName = string;
type queueUrl = string;
export type TQueueLookup = Record<sstResourceName, queueUrl>
type TQueueManifestSection = {
  qs: TQueueLookup
  dlqs: TQueueLookup
}
export type TQueueManifest = {
  normal: TQueueManifestSection;
  segmented: TQueueManifestSection;
}

export const FILE_INGESTION_SIZE_LIMIT = 1000 * 1000 * 10; // 10MB

export const fileSizeLimitByType: Record<string, number> = {
  "xlsx": 1000 * 200, // 200KB
  "csv": 1000 * 200, // 200KB
} as const

export const bdaMaxChunkChars = 600 * 4
export const bdaMaxChunkOverlapChars = 280
export const bdaIsolateTableWhenChars = 2000
export const bdaMaxSingleTableChunkChars = 7000

export const klMetadataSchema = z.object({
  type: z.enum(["kls-document", "kls-web-page"] as const),
});

export type KLMetadata = z.infer<typeof klMetadataSchema>;

export const guardKLMetadata = (thing: unknown, logger: TLogger): thing is KLMetadata => {
  const parseResult = klMetadataSchema.safeParse(thing);
  if (!parseResult.success) {
    logger.warn({ message: `failed to parse KLMetadata: ${parseResult.error}`, details: thing });
  }

  return parseResult.success;
}

export const klsSourceMetadataDocumentSchema = z.object({
  type: z.literal("kls-document"),
  updatedTs: z.number(),
  filename: z.string(),
  fileExtension: z.string().optional(),
  size: z.number(),
  contentType: z.string(),
});

export type KLSDocumentMetadata = z.infer<typeof klsSourceMetadataDocumentSchema>;

export const klsSourceMetadataWebPageSchema = z.object({
  type: z.literal("kls-web-page"),
  updatedTs: z.number(),
  websiteId: z.string(),
  url: z.string(),
  baseUrl: z.string(),
  contentType: z.string(),
});

export type KLSWebPageMetadata = z.infer<typeof klsSourceMetadataWebPageSchema>;

export const klSourceMetadataSchema = z.discriminatedUnion("type", [
  klsSourceMetadataDocumentSchema,
  klsSourceMetadataWebPageSchema,
])

export type KLSourceMetadata = z.infer<typeof klSourceMetadataSchema>;

export const guardKLSDocumentMetadata = (thing: unknown): thing is KLSDocumentMetadata => {
  return typeof thing === 'object' && thing !== null && 'type' in thing && thing.type === 'kls-document'
}

export const guardKLSWebPageMetadata = (thing: unknown): thing is KLSWebPageMetadata => {
  return typeof thing === 'object' && thing !== null && 'type' in thing && thing.type === 'kls-web-page'
}

export const guardKLSourceMetadata = (thing: unknown): thing is KLSourceMetadata => {
  return guardKLSDocumentMetadata(thing) || guardKLSWebPageMetadata(thing)
}

const klsExtractionStatus = ['pending', 'extracting', 'extracted', 'failed'] as const;
const klsIngestionStatus = ['pending', 'ingesting', 'ingested', 'failed'] as const;
export type KLSExtractionStatus = typeof klsExtractionStatus[number];
export type KLSIngestionStatus = typeof klsIngestionStatus[number];

export const guardKLSExtractionStatus = (thing: unknown): thing is KLSExtractionStatus => {
  return typeof thing === 'string' && klsExtractionStatus.includes(thing as KLSExtractionStatus);
}

export const guardKLSIngestionStatus = (thing: unknown): thing is KLSIngestionStatus => {
  return typeof thing === 'string' && klsIngestionStatus.includes(thing as KLSIngestionStatus);
}

export const klSourceWithoutMetadataSchema = z.object({
  id: z.string().min(1),
  klId: z.string().min(1),
  extractionStatus: z.enum(klsExtractionStatus),
  extractionDetails: z.string().nullish(),
  ingestionStatus: z.enum(klsIngestionStatus),
  ingestionDetails: z.string().nullish(),
  extractionLastUpdatedTs: z.date(),
  ingestionLastUpdatedTs: z.date(),
  createdAutoTs: z.date(),
  updatedAutoTs: z.date(),
})

export const klSourceSchema = klSourceWithoutMetadataSchema.extend({
  metadata: klSourceMetadataSchema,
})

export type KLSource = z.infer<typeof klSourceSchema>

export const isKLSource = (thing: unknown): thing is KLSource => {
  return typeof thing === 'object' && thing !== null && 'id' in thing && 'klId' in thing && 'metadata' in thing
}

export const klSourceDocumentSchema = klSourceWithoutMetadataSchema.extend({
  metadata: klsSourceMetadataDocumentSchema,
})

export type KLSourceDocument = z.infer<typeof klSourceDocumentSchema>

export const isKLSourceDocument = (thing: unknown): thing is KLSourceDocument => {
  return isKLSource(thing) && guardKLSDocumentMetadata(thing.metadata)
}

export const klSourceWebPageSchema = klSourceWithoutMetadataSchema.extend({
  metadata: klsSourceMetadataWebPageSchema,
})

export type KLSourceWebPage = z.infer<typeof klSourceWebPageSchema>

export const isKLSourceWebPage = (thing: unknown): thing is KLSourceWebPage => {
  return isKLSource(thing) && guardKLSWebPageMetadata(thing.metadata)
}

export interface ResourceProvider {
  getSegmentedTopicArn: (prefix: string, suffix: string, segment: number) => string;
  getDashboardCommandTopicArn: () => string;
  getIngestMatterTopicArn: () => string;
  getIngestAllMattersTopicArn: () => string;
  getMassIngestionTopicArn: () => string;
  getIncomingFileTopicArn: () => string;
  getMetricsProcessorTopicArn: () => string;
  getRegularFilteringTopicArn: () => string;
  getDetExtEvaluatorTopicArn: () => string;
  getCustomExtEmptyContentTopicArn: () => string;
  getUnknownDocTypesTableName: () => string;
  getIngestionMetadataTableName: () => string;
  getChunkListOverflowBucketName: () => string;
  getFileChunksTableName: () => string;
  getFileMetadatasTableName: () => string;
  getEmailDateFunctionName: () => string;
  getDedupeTableName: () => string;
  getMatterStatusTableName: () => string;
  getAccountStatusTableName: () => string;
  getIngestionTrackingTableName: () => string;
  getAmplitudeApiKey: () => string;
  getMassVeryLargeFilesQueueUrl: () => string;
  getMassLargeFilesQueueUrl: () => string;
  getRegVeryLargeFilesQueueUrl: () => string;
  getRegLargeFilesQueueUrl: () => string;
  getFastTrackFilesQueueUrl: () => string;
  getExtractedTextBucketName: () => string;
  getKnowledgeLibrariesBucketName: () => string;
  getAzureOpenAiKey: () => string;
  getOpenAIApiKey: () => string;
  getPgVectorPassword: () => string;
  getPgVectorReadOnlyPassword: () => string;
  getSmokeballApiKey: () => string;
  getWebQueryRDSEndpoint: () => string;
  getWebQueryRDSUser: () => string;
  getWebQueryRDSPassword: () => string;
  getMatterManagementRDSEndpoint: () => string;
  getMatterManagementRDSUser: () => string;
  getMatterManagementRDSPassword: () => string;
  getArchieRDSEndpoint: () => string;
  getArchieRDSUser: () => string;
  getArchieRDSPassword: () => string;
  getArchieDb: () => string;
  getIngestionProcessingUpdatesTopicArn: () => string;
}


export const getLocalStageForDb = () => (process.env["SST_DEV"] && process.env["SST_STAGE"] && !process.env["FORCE_DEVAUS_STAGE"])
  ? process.env["SST_STAGE"]
  : null;

const ALLOWED_KL_EXTENSIONS = [".pdf", ".docx", ".txt", ".md"] as const;
const ALLOWED_KL_CONTENT_TYPES = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "text/plain", "text/markdown"] as const;

export type AllowedKlExtension = typeof ALLOWED_KL_EXTENSIONS[number];
export type AllowedKlContentType = typeof ALLOWED_KL_CONTENT_TYPES[number];

export const guardIsAllowedKlExtension = (extension: unknown): extension is AllowedKlExtension => {
  if (typeof extension === 'string') {
    return ALLOWED_KL_EXTENSIONS.includes(extension as AllowedKlExtension);
  }
  return false;
};

export const guardIsAllowedKlContentType = (contentType: unknown): contentType is AllowedKlContentType => {
  if (typeof contentType === 'string') {
    return ALLOWED_KL_CONTENT_TYPES.includes(contentType as AllowedKlContentType);
  }
  return false;
};

const contentTypeMap: Record<AllowedKlExtension, AllowedKlContentType> = {
  '.pdf': 'application/pdf',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.txt': 'text/plain',
  '.md': 'text/markdown'
} as const;

export const getKlContentType = (filenameOrExtensionWithDot: string): AllowedKlContentType | null => {
  // Extract the file extension
  const lastDotIndex = filenameOrExtensionWithDot.lastIndexOf('.');
  const extension = (lastDotIndex === -1) ? `.${filenameOrExtensionWithDot}` : filenameOrExtensionWithDot.toLowerCase().substring(lastDotIndex);

  if (!guardIsAllowedKlExtension(extension)) {
    return null;
  }


  return contentTypeMap[extension] || null;
};
